import type {
  ComponentSettings,
  ComponentSettingsProps,
} from "@repo/editor/externalComponents";
import { forwardRef } from "react";
import * as t from "@rekajs/types";
import type { ComponentType } from "@repo/editor/types";
import { cn } from "../libs/utils";
import { css, styled } from "../stitches.config";

export const TextDefinition = {
  size: {
    type: "enum",
    options: [
      {
        value: "sm",
        label: "sm",
      },
      {
        value: "base",
        label: "base",
      },
      {
        value: "lg",
        label: "lg",
      },
      {
        value: "xl",
        label: "xl",
      },
      {
        value: "2xl",
        label: "2xl",
      },
    ],
    default: "base",
    label: "سایز",
  },
  color: {
    type: "colorPicker",
    default: "#000000",
    label: "رنگ",
  },
  value: {
    type: "html",
    default: "لورم ایپسوم یک متن تستی است",
    label: "متن",
  },
  lineHeight: {
    type: "style",
    default: 1.5,
    label: "فاصله خط",
  },
  padding: {
    type: "object",
    properties: {
      mobile: {
        type: "style",
        default: "16px",
        label: "پدینگ موبایل",
      },
      tablet: {
        type: "style",
        default: "24px",
        label: "پدینگ تبلت",
      },
      laptop: {
        type: "style",
        default: "32px",
        label: "پدینگ لپتاپ",
      },
    },
    label: "پدینگ",
  },
};

export interface TextProps {
  size?: "sm" | "base" | "lg" | "xl" | "2xl";
  color?: string;
  value?: string;
  lineHeight?: string;
  padding?: {
    mobile?: string;
    tablet?: string;
    laptop?: string;
  };
}

const StyledDiv = styled("div", {
  color: "var(--text-color, #000000)",
  lineHeight: "var(--text-line-height, 1.5)",
  padding: "var(--text-sm-padding, 0)",
  "@media (min-width: 768px)": {
    padding: "var(--text-md-padding, 0)",
  },
  "@media (min-width: 1024px)": {
    padding: "var(--text-lg-padding, 0)",
  },
});

const dynamicColor = css({
  color: "var(--text-color)",
});

export const Text = forwardRef<HTMLDivElement, TextProps>((props, ref) => {
  const sizes = {
    sm: "text-sm lg:text-base",
    base: "text-base lg:text-lg",
    lg: "text-lg lg:text-xl",
    xl: "text-xl lg:text-2xl",
    "2xl": "text-2xl lg:text-4xl",
  };

  const className = sizes[props.size || "base"];

  return (
    <StyledDiv
      className={cn(className, dynamicColor(), "font-helvetica rtl:font-doran")}
      dangerouslySetInnerHTML={{ __html: props.value || "" }}
      ref={ref}
      style={
        {
          "--text-color": props.color || "#000000",
          "--text-line-height": props.lineHeight || 1.5,
          "--text-sm-padding": props.padding?.mobile || "0",
          "--text-md-padding": props.padding?.tablet || "0",
          "--text-lg-padding": props.padding?.laptop || "0",
        } as React.CSSProperties
      }
    />
  );
}) as ComponentType<TextProps>;

Text.displayName = "Text";

export const TextSettings: ComponentSettings = {
  name: "EText",
  props: TextDefinition as ComponentSettingsProps,
  label: "متن",
  widgets: {
    label: "متن",
    template: () =>
      t.componentTemplate({
        props: {
          value: t.literal({ value: "لورم ایپسوم یک متن تستی است" }),
          padding: t.objectExpression({
            properties: {
              mobile: t.literal({ value: "16px" }),
              tablet: t.literal({ value: "24px" }),
              laptop: t.literal({ value: "32px" }),
            },
          }),
        },
        component: t.identifier({ name: "EText", external: true }),
      }),
  },
};

Text.componentSettings = TextSettings;
